/**
 * ServiceForm Component
 *
 * Reusable form for creating and editing services
 */

import { useRef } from 'react';
import {
  TextField,
  SelectField,
  TeamAutocompleteField,
  type CreateServiceFormData,
} from '../forms';
import { createServiceSchema, editServiceSchema } from '../forms/validation';
import { EntityForm } from '../shared';
import { ScanManagedBanner, ScanManagedFieldNote } from './ScanLinkage';
import type { ScanManagedField } from '../../graphql/types';
import { ServiceType, ServiceLifecycleStatus, ServiceTier } from '../../graphql/types';
import { slugify } from '../../lib/slugify';
import { useAccessibleTeams } from '../../hooks/useAccessibleTeams';
import '@tylertech/forge/card';
import './ServiceForm.css';

export interface ServiceFormProps {
  /** Initial form values (for edit mode) */
  defaultValues?: Partial<CreateServiceFormData>;
  /** Submit handler */
  onSubmit: (data: CreateServiceFormData) => void | Promise<void>;
  /** Cancel handler */
  onCancel: () => void;
  /** Is form submitting */
  isSubmitting?: boolean;
  /** Form mode */
  mode: 'create' | 'edit';
  /**
   * Scan-managed field manifest of a scan-linked Service (edit mode, issue
   * #2000). Empty/omitted shows no banner or indicators.
   */
  scanManagedFields?: ScanManagedField[] | null;
  /** Source repo of the scan, shown in the banner. */
  scanRepo?: string | null;
}

const CardElement = 'forge-card' as React.ElementType;

/**
 * Service create/edit form with validation
 *
 * @example
 * ```tsx
 * <ServiceForm
 *   mode="create"
 *   onSubmit={handleCreate}
 *   onCancel={() => navigate('/services')}
 * />
 * ```
 */
export function ServiceForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
  scanManagedFields,
  scanRepo,
}: ServiceFormProps): JSX.Element {
  // Tracks whether the user has manually edited the Short Name. Once they do,
  // we stop auto-syncing it from the Service Name (issue #333). A ref (not
  // state) is used because this only gates an event handler — it must not
  // trigger a re-render.
  const shortNameTouched = useRef(false);

  // Scope the dropdown to teams the user can access; always keep the
  // service's existing team selectable in edit mode even if the user isn't a
  // member of it (issue #649).
  const { teams: accessibleTeams } = useAccessibleTeams(defaultValues?.teamId);
  const teamOptions = accessibleTeams.map((team) => ({ value: team.entityId, label: team.name }));

  return (
    <EntityForm<CreateServiceFormData>
      schema={mode === 'edit' ? editServiceSchema : createServiceSchema}
      baseValues={{
        lifecycleStatus: 'development',
        tier: 'tier2',
      }}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      mode={mode}
      createLabel="Create Service"
      formClassName="service-form"
    >
      {({ register, setValue, formState: { errors }, values }) => {
        const nameField = register('name');
        const shortNameField = register('shortName');

        // Auto-prefill Short Name from Service Name in create mode only, until
        // the user takes over the Short Name field. In edit mode the existing
        // short name is preserved and never auto-rewritten.
        const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
          void nameField.onChange(event);
          if (mode === 'create' && !shortNameTouched.current) {
            setValue('shortName', slugify(event.target.value), {
              shouldValidate: true,
            });
          }
        };

        // Mark the Short Name as user-owned once they edit it directly so later
        // Service Name changes leave it untouched.
        const handleShortNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
          shortNameTouched.current = true;
          void shortNameField.onChange(event);
        };

        const isScanLinked = mode === 'edit' && Boolean(scanManagedFields?.length);
        const managed = isScanLinked ? scanManagedFields : null;

        return (
          <>
            {isScanLinked && <ScanManagedBanner repo={scanRepo} />}
            <CardElement className="form-card form-card--surface">
              <div className="form-section form-section--padded">
                <h2 className="forge-typography--headline6">Basic Information</h2>

                <TextField
                  label="Service Name"
                  {...nameField}
                  onChange={handleNameChange}
                  value={values.name || ''}
                  error={errors.name}
                  required
                  placeholder="e.g., Payments Ledger Service"
                  helperText="A descriptive name for the service"
                />
                <ScanManagedFieldNote fields={managed} field="name" />

                <TextField
                  label="Short Name"
                  {...shortNameField}
                  onChange={handleShortNameChange}
                  value={values.shortName || ''}
                  error={errors.shortName}
                  required
                  placeholder="e.g., payments-ledger"
                  helperText="URL-friendly identifier (lowercase, hyphens only). Auto-filled from the Service Name until you edit it."
                />

                <TextField
                  label="Description"
                  {...register('description')}
                  value={values.description || ''}
                  error={errors.description}
                  placeholder="Brief description of the service"
                  helperText="Optional description (max 1000 characters)"
                />
                <ScanManagedFieldNote fields={managed} field="description" />
              </div>
            </CardElement>

            <CardElement className="form-card form-card--surface">
              <div className="form-section form-section--padded">
                <h2 className="forge-typography--headline6">Classification</h2>

                <SelectField
                  label="Service Type"
                  {...register('serviceType')}
                  value={values.serviceType}
                  options={[
                    { value: ServiceType.Application, label: 'Application' },
                    { value: ServiceType.Integration, label: 'Integration' },
                    { value: ServiceType.Platform, label: 'Platform' },
                    { value: ServiceType.Infrastructure, label: 'Infrastructure' },
                    { value: ServiceType.Website, label: 'Website' },
                  ]}
                  error={errors.serviceType}
                  required
                />
                <ScanManagedFieldNote fields={managed} field="serviceType" />

                <SelectField
                  label="Lifecycle Status"
                  {...register('lifecycleStatus')}
                  value={values.lifecycleStatus}
                  options={[
                    { value: ServiceLifecycleStatus.Unassessed, label: 'Not yet assessed' },
                    { value: ServiceLifecycleStatus.Prototype, label: 'Prototype' },
                    { value: ServiceLifecycleStatus.Development, label: 'Development' },
                    { value: ServiceLifecycleStatus.Production, label: 'Production' },
                    { value: ServiceLifecycleStatus.Deprecated, label: 'Deprecated' },
                    { value: ServiceLifecycleStatus.Retired, label: 'Retired' },
                  ]}
                  error={errors.lifecycleStatus}
                />

                <SelectField
                  label="Tier"
                  {...register('tier')}
                  value={values.tier}
                  options={[
                    { value: ServiceTier.Unassigned, label: 'Not yet assigned' },
                    { value: ServiceTier.Tier0, label: 'Tier 0 (Critical)' },
                    { value: ServiceTier.Tier1, label: 'Tier 1 (High Priority)' },
                    { value: ServiceTier.Tier2, label: 'Tier 2 (Medium Priority)' },
                    { value: ServiceTier.Tier3, label: 'Tier 3 (Low Priority)' },
                  ]}
                  error={errors.tier}
                  required
                />
              </div>
            </CardElement>

            <CardElement className="form-card form-card--surface">
              <div className="form-section form-section--padded">
                <h2 className="forge-typography--headline6">Organization</h2>

                <TeamAutocompleteField
                  name="teamId"
                  label="Team"
                  options={teamOptions}
                  value={values.teamId || ''}
                  onChange={(teamId) => setValue('teamId', teamId, { shouldValidate: true })}
                  error={errors.teamId}
                  required={mode === 'create'}
                  placeholder="Search teams…"
                  helperText="Only teams you have access to are shown"
                />
                <ScanManagedFieldNote
                  fields={managed}
                  field="teamId"
                  changed={(values.teamId || '') !== (defaultValues?.teamId ?? '')}
                />
              </div>
            </CardElement>

            <CardElement className="form-card form-card--surface">
              <div className="form-section form-section--padded">
                <h2 className="forge-typography--headline6">Technical Details</h2>

                <TextField
                  label="Repository URL"
                  type="url"
                  {...register('repo')}
                  value={values.repo || ''}
                  error={errors.repo}
                  placeholder="https://github.com/org/repo"
                  helperText="Optional Git repository URL"
                />
                <ScanManagedFieldNote fields={managed} field="repo" />

                <TextField
                  label="Website URL"
                  type="url"
                  {...register('url')}
                  value={values.url || ''}
                  error={errors.url}
                  placeholder="https://www.example.gov"
                  helperText="Optional hosted-site URL — the identity of a Website service (e.g. a CMS-hosted site with no repository)"
                />
                <ScanManagedFieldNote fields={managed} field="url" />
              </div>
            </CardElement>
          </>
        );
      }}
    </EntityForm>
  );
}
